import {
  AUDIT_STATUS,
  DEFAULT_FINDING_REPORT_TYPE,
  FINDING_DYNAMIC_SEVERITY,
  resolveAuditStatus,
} from "../../const";
import {
  getGroupsToInsertAndReformattedCollabList,
  updateCollaboratorListForMentionWithGroups,
} from "./userGroup";
import CryptoJS from "crypto-js";
import { bech32 } from "bech32";

/**
 * checks if the given email address is valid
 * @param {string} email the given email address to be examined
 * @returns {boolean} the result
 */
export const validateEmailRegex = (email) => {
  try {
    const re =
      /^([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email.toLowerCase());
  } catch (err) {
    console.error(err.message);
    return false;
  }
};

/**
 * checks if the given string contains any upper case letters
 * @param {string} string the given string to be examined
 * @returns {boolean} the result
 */
export const validateUpperCaseLetterExistsRegex = (string) => {
  const re = /^(?=.*[A-Z])/;
  return re.test(string);
};

/**
 * checks if the given string contains any lower case letters
 * @param {string} string the given string to be examined
 * @returns {boolean} the result
 */
export const validateLowerCaseLetterExistsRegex = (string) => {
  const re = /^(?=.*[a-z])/;
  return re.test(string);
};

/**
 * checks if the given string contains any special characters (!@#$%^&*)
 * @param {string} string the given string to be examined
 * @returns {boolean} the result
 */
export const validateSpecialCharacterExistsRegex = (string) => {
  const re = /^(?=.*[!@#$%^&*])/;
  return re.test(string);
};

/**
 * Omit properties from an object
 * @param {string[]} props
 * @param {Object} obj
 * @returns {Object}
 * @example
 * ```js
 * omit(["dogs"], { cats: 2, dogs: 5 }) // { cats: 2 }
 * ```
 */
export function omit(props = [], obj) {
  if (
    obj == null ||
    ["string", "number", "bigint", "boolean", "symbol", "undefined"].includes(typeof obj)
  ) {
    return obj;
  }
  const keys = Object.keys(obj);
  return keys
    .filter((key) => !props.includes(key))
    .reduce((acc, prop) => ({ ...acc, [prop]: obj[prop] }), {});
}

export function findingTally(findingList, reportType = DEFAULT_FINDING_REPORT_TYPE) {
  const findingCountMap = {};

  if (
    !findingList ||
    !findingList.length ||
    !reportType ||
    !(reportType in FINDING_DYNAMIC_SEVERITY)
  )
    return findingCountMap;
  const aliasMap = FINDING_DYNAMIC_SEVERITY[reportType]?.["ALIAS_MAP"];
  const severityMap = FINDING_DYNAMIC_SEVERITY[reportType]?.["SEVERITY_MAP"];
  findingList.forEach((item) => {
    // item.severity might use alias name such as `Critical, Major...` instead of regular all lowercase severity string
    // Here to formalize the severity string according to aliasMap
    if (!item.severity || item.severity in severityMap !== true) {
      if (item.severity && item.severity in aliasMap) item.severity = aliasMap[item.severity];
      else return;
    }
    findingCountMap[item.severity] = (findingCountMap[item.severity] || 0) + 1;
  });
  return findingCountMap;
}

/**
 * conditionally pluralizes a word based on a count
 * @param {string} word the word to pluralize
 * @param {number} count how many of the item there are
 * @param {string?} suffix the suffix to add
 * @param {boolean?} replace replace the whole word
 * @returns
 */
export function pluralize(word, count, suffix = "s", replace = false) {
  return count !== 1 ? (replace ? suffix : word + suffix) : word;
}

/**
 * `capitalize` takes in a string and returns the capitalized string
 * @param {string} word the word to capitalize
 * @returns {string} the capitalized word
 */
export function capitalize(word) {
  return typeof word === "string" ? word.charAt(0).toUpperCase() + word.slice(1) : word; // if word is "", charAt() and slice() will both return ""
}

const range = (start, stop) => [...Array(stop - start)].map((x, i) => start + i);

export const chunk = (n) => (arr) => {
  return range(0, Math.ceil(arr.length / n)).map((_, i) => arr.slice(i * n, i * n + n));
};

/**
 * Wrapper around `Promise.all` for catching errors
 * @param {Promise<any>[]} promiseArray
 * @returns {Promise<any[]>} array of results
 */
export async function promiseAll(promiseArray) {
  return await Promise.all(
    promiseArray.map((promise) =>
      promise instanceof Promise
        ? promise
            .then((result) => ({
              result,
            }))
            .catch((error) => ({
              result: null,
              error: error.message,
            }))
        : {
            result: promise,
          }
    )
  );
}

/**
 * Wrapper around `promiseAll` for logging errors
 * @param {Promise<any>[]} promiseArray
 * @param {any} loggerObj - Logger object e.g. Logger (default) / console
 * @returns {Promise<any[]>}
 */
export async function promiseAllWithErrorLogging(promiseArray, loggerObj = console) {
  const resultArray = await promiseAll(promiseArray);

  // Log promise results with error objects
  resultArray.forEach((resObj) => {
    if (resObj?.error) {
      const errorLog = "Error caught from a promise in promiseAll";
      loggerObj.log(errorLog, resObj, "error");
    }
  });

  return resultArray;
}

/**
 * Encodes special characters in each path of the given url into utf-8 format.
 * Note: can not deal with urls with query string at the moment.
 * @param {string} srcUrl the url to be encoded
 * @returns {string} encoded url
 */
export function encodeHttpUrl(srcUrl) {
  try {
    const url = new URL(srcUrl);
    const [, path] = srcUrl?.split(url?.origin);
    const encodedPathname = path
      .split("/")
      .map((route) => encodeURIComponent(route))
      .join("/");
    return url.origin + encodedPathname;
  } catch (err) {
    console.error(err);
    return "";
  }
}

export function isHttpUrlEncoded(url) {
  try {
    const decodedUrl = decodeURIComponent(url);
    return decodedUrl !== url;
  } catch (error) {
    return false; // Error occurred during decoding (e.g., invalid encoding)
  }
}

export function getFileNameFromUrl(url) {
  if (typeof url !== "string" || url.trim() === "") {
    return "";
  }

  let pathname = url;

  try {
    pathname = new URL(url).pathname;
  } catch (error) {
    pathname = url;
  }

  const [pathWithoutSearchOrHash] = pathname.split(/[?#]/);
  if (pathWithoutSearchOrHash.endsWith("/")) {
    return "";
  }

  const fileName = pathWithoutSearchOrHash.split("/").filter(Boolean).pop() || "";

  if (fileName === "") {
    return "";
  }

  try {
    return decodeURIComponent(fileName);
  } catch (error) {
    return fileName;
  }
}

export function mentionListAdapter(collaboratorsList, projectTenantName) {
  // If the current collaborator list contains at least one collaborator with a role that has an associated assignee group and is not pending,
  // 1. Store assignee role group object into `groupsToInsert` to be inserted into reformattedCollaboratorList later on
  // 2. Remove individual users with the role
  let { groupsToInsert, reformattedCollaboratorList } =
    getGroupsToInsertAndReformattedCollabList(collaboratorsList);

  reformattedCollaboratorList = reformattedCollaboratorList.map((collaborator) => {
    const collaboratorTenantName = collaborator.tenantName || projectTenantName;
    if (collaborator.isPending) {
      return {
        id: collaborator.userId,
        value: `${collaborator.userId?.split("-")[0]} (pending user)`,
        role: collaborator?.role,
        userId: collaborator.userId, // For compatibility
      };
    }
    switch (collaborator.role) {
      case "certikBd":
        return {
          id: collaborator.userId,
          value: `${collaborator.userName}  (CertiK BD)`,
          role: collaborator.role,
          userId: collaborator.userId, // For compatibility
        };
      case "certikDev":
        return {
          id: collaborator.userId,
          value: `${collaborator.userName}  (CertiK Eng)`,
          role: collaborator.role,
          userId: collaborator.userId, // For compatibility
        };
      case "certikSupport":
        return {
          id: collaborator.userId,
          value: `${collaborator.userName}  (CertiK Support)`,
          role: collaborator.role,
          userId: collaborator.userId, // For compatibility
        };
      case "certikAdmin":
        return {
          id: collaborator.userId,
          value: `${collaborator.userName}  (CertiK Admin)`,
          role: collaborator.role,
          userId: collaborator.userId, // For compatibility
        };
      case "exchangeAdmin":
        return {
          id: collaborator.userId,
          value: `${collaborator.userName}  (${collaboratorTenantName} Admin)`,
          role: collaborator.role,
          userId: collaborator.userId, // For compatibility
        };
      case "exchangeMember":
        return {
          id: collaborator.userId,
          value: `${collaborator.userName}  (${collaboratorTenantName} Member)`,
          role: collaborator.role,
          userId: collaborator.userId, // For compatibility
        };
      case "exchangeAssignee":
        return {
          id: collaborator.userId,
          value: `${collaborator.userName}  (${collaboratorTenantName} Restricted Member)`,
          role: collaborator.role,
          userId: collaborator.userId, // For compatibility
        };
    }
  });

  // If there are groups to insert, place them at the start of the mention list
  return updateCollaboratorListForMentionWithGroups(reformattedCollaboratorList, groupsToInsert);
}

/* Check whether a project status needs to be rolled back when a new final report is published
 * Prerequisite 1: project is final-report-signed-off or completed
 * Prerequisite 2: project requests to publish or already has got a published link
 * */
export function auditStatusRollbackPrerequisite(projectBlob) {
  return (
    [AUDIT_STATUS.FINAL_REPORT_SIGNED_OFF, AUDIT_STATUS.COMPLETED].includes(
      resolveAuditStatus(projectBlob?.status)
    ) && projectBlob?.clientRequestToPublish === true
  );
}

export function encrypt(message) {
  const encrypted = CryptoJS.AES.encrypt(message, process.env.AES_KEY);
  return encrypted.toString();
}

export function decrypt(message) {
  const code = CryptoJS.AES.decrypt(message, process.env.AES_KEY);
  const decryptedMessage = code.toString(CryptoJS.enc.Utf8);

  return decryptedMessage;
}

const CERTIK_CHAIN_ADDRESS_PREFIX = "certik";

/**
 * checks if the given value is a valid certik chain account address
 * @param {string} value the value to be examined
 * @returns {boolean} the boolean result
 */
export function validateCertikChainAddress(value) {
  const re = new RegExp(`^${CERTIK_CHAIN_ADDRESS_PREFIX}[0-9,a-z]{39}$`);
  if (re.test(value) !== true) {
    return false;
  }
  try {
    const decodedBech32 = bech32.decode(value);
    return (
      decodedBech32?.prefix === CERTIK_CHAIN_ADDRESS_PREFIX && decodedBech32?.words?.length === 32
    );
  } catch (err) {
    console.error(err);
    return false;
  }
}

/**
 * Checks if input string follows valid twitter username formatting
 * @param {string} twitterUsername the input string
 * @returns {boolean} true if input string is valid
 */
export function twitterUsernameFormatValidator(twitterUsername) {
  return /^@(\w){4,15}$/.test(twitterUsername);
}

/**
 * Checks if input string follows valid telegram username formatting
 * @param {string} telegramUsername the input string
 * @returns {boolean} true if input string is valid
 */
export function telegramUsernameFormatValidator(telegramUsername) {
  return /^@(\w){5,}$/.test(telegramUsername);
}

export function convertTZ(date, tzString) {
  const localeOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    timeZoneName: "short",
    timeZone: tzString,
  };
  return new Date(typeof date === "string" ? new Date(date) : date).toLocaleDateString(
    "en",
    localeOptions
  );
}

const validateHttps = (protocol) => {
  if (!protocol.match("https")) {
    return { passed: false, reason: "Protocol must be HTTPS" };
  }
  return { passed: true };
};

function debugUrl(value) {
  const modifications = [
    {
      tester: (value) =>
        !value.includes(":") || ["#", "&", "=", "/", "_", "*", " "].includes(value.split(":")[0]),
      reason: "Missing protocol",
      modifier: (value) => `https://${value}`,
    },
  ];

  const check = (val) => {
    try {
      new URL(val);
      return true;
    } catch (e) {
      return false;
    }
  };

  if (check(value)) {
    return { passed: true };
  }

  for (const modification of modifications) {
    if (modification.tester(value) && check(modification.modifier(value))) {
      return { passed: false, reason: modification.reason };
    }
  }
  return { passed: false, reason: "Invalid URL (must be full a url)" };
}

/**
 * Generates a url validator with the supplied rules
 * @param {Object} options
 * @param {string|RegExp|(host: string) => { passed: true } | { passed: false, reason: string }} options.host matcher/validator for the url host
 * @param {string|RegExp|(pathname: string) => { passed: true } | { passed: false, reason: string }} options.pathname matcher/validator for the url path
 * @param {string|RegExp|(origin: string) => { passed: true } | { passed: false, reason: string }} options.origin matcher/validator for the url origin
 * @param {string|RegExp|(searchParams: URLSearchParams) => { passed: true } | { passed: false, reason: string }} options.searchParams matcher/validator for the url search params
 * @param {string|RegExp|(protocol: string) => { passed: true } | { passed: false, reason: string }} options.protocol matcher/validator for the url protocol
 * @returns {(testUrl: string) => { passed: true } | { passed: false, reason: string }} validator
 */
export function generateUrlValidator(options) {
  const {
    host = /.*/gi,
    pathname = /.*/gi,
    origin = /.*/gi,
    searchParams = /.*/gi,
    protocol = validateHttps,
  } = {
    ...(options || {}),
  };
  return (testUrl) => {
    try {
      const url = new URL(testUrl);
      if (origin) {
        if (typeof origin === "function") {
          const res = origin(url.origin);
          if (!res.passed) {
            return res;
          }
        } else if (typeof origin === "string" || origin instanceof RegExp) {
          if (!url.origin.match(origin)) {
            return { passed: false, reason: `URL origin must match ${origin}` };
          }
        }
      }
      if (host) {
        let hostname = url.host || url.hostname;
        if (typeof host === "function") {
          const res = host(hostname);
          if (!res.passed) {
            return res;
          }
        } else if (typeof host === "string" || host instanceof RegExp) {
          if (!hostname.match(host)) {
            return { passed: false, reason: `URL host must match ${host}` };
          }
        }
      }

      if (pathname) {
        if (typeof pathname === "function") {
          const res = pathname(url.pathname);
          if (!res.passed) {
            return res;
          }
        } else if (typeof pathname === "string" || pathname instanceof RegExp) {
          if (!url.pathname.match(pathname)) {
            return { passed: false, reason: `URL pathname must match ${pathname}` };
          }
        }
      }

      if (protocol) {
        if (typeof protocol === "function") {
          const res = protocol(url.protocol);
          if (!res.passed) {
            return res;
          }
        } else if (typeof protocol === "string" || protocol instanceof RegExp) {
          if (!url.protocol.match(protocol)) {
            return { passed: false, reason: `URL protocol must match ${protocol}` };
          }
        }
      }

      if (searchParams && typeof searchParams === "function") {
        const res = searchParams(url.searchParams);
        if (!res.passed) {
          return res;
        }
      }

      return { passed: true };
    } catch (e) {
      console.error(e);
      return debugUrl(testUrl);
    }
  };
}

export const coinGeckoValidator = generateUrlValidator({
  host: (host) => {
    const coinGeckoHostName = "www.coingecko.com";
    if (host !== coinGeckoHostName) {
      return {
        passed: false,
        reason: `Invalid host, expected '${coinGeckoHostName}' but got '${host}'`,
      };
    }
    return { passed: true };
  },
  pathname: (pathname) => {
    const path = pathname.split("/");
    if (!path.includes("coins")) {
      return { passed: false, reason: "Supplied url does not link to a coin" };
    } else if (path.filter((val) => val !== "coins").length === 0) {
      return { passed: false, reason: "Token ID is missing from URL" };
    }
    return { passed: true };
  },
});

export const coinMarketCapValidator = generateUrlValidator({
  host: (host) => {
    const coinMarketCapHostName = "coinmarketcap.com";
    if (host !== coinMarketCapHostName) {
      return {
        passed: false,
        reason: `Invalid host, expected '${coinMarketCapHostName}' but got '${host}'`,
      };
    }
    return { passed: true };
  },
  pathname: (pathname) => {
    const path = pathname.split("/");
    if (!path.includes("currencies")) {
      return { passed: false, reason: "Supplied url does not link to a coin" };
    } else if (path.filter((val) => val !== "coins").length === 0) {
      return { passed: false, reason: "Token ID is missing from URL" };
    }
    return { passed: true };
  },
});

export const twitterLinkValidator = generateUrlValidator({
  host: (host) => {
    if (host !== "twitter.com" && host !== "x.com") {
      return {
        passed: false,
        reason: `Invalid host, expected twitter.com or x.com, but got '${host}'`,
      };
    }
    return { passed: true };
  },
  pathname: (pathname) => {
    const path = pathname.split("/");
    if (path.filter((val) => val !== "").length === 0) {
      return { passed: false, reason: "Twitter username is missing from URL" };
    }
    return { passed: true };
  },
});

export const telegramLinkValidator = generateUrlValidator({
  host: (host) => {
    const telegramHostName = "t.me";
    if (host !== telegramHostName) {
      return {
        passed: false,
        reason: `Invalid host, expected '${telegramHostName}' but got '${host}'`,
      };
    }
    return { passed: true };
  },
  pathname: (pathname) => {
    const path = pathname.split("/");
    if (path.filter((val) => val !== "").length === 0) {
      return { passed: false, reason: "Telegram username / groupname is missing from URL" };
    }
    return { passed: true };
  },
});
